import { IconProps } from '@atlaskit/icon';
import ArrowDownIcon from '@atlaskit/icon/core/migration/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/migration/arrow-up';
import BoardIcon from '@atlaskit/icon/core/migration/board';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import MediaServicesGridIcon from '@atlaskit/icon/core/migration/grid--media-services-grid';
import UnlinkIcon from '@atlaskit/icon/core/migration/link-broken--unlink';
import NewTableIcon from '@atlaskit/icon/core/spreadsheet';
import OldTableIcon from '@atlaskit/icon/glyph/table';
import { messages as globalMessages } from '@databases/intl/messages';
import { DefaultRootState } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseLayoutType, DatabaseView } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { DatabaseChange } from '@databases/utils/database/changesets';
import { ViewChangeType, getViewChange } from '@databases/utils/database/view/view';
import { isStandalone } from '@databases/utils/utils';
import React, { ReactElement } from 'react';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import LayoutIcon from '../components/layoutIcon/LayoutIcon';
import { getLayoutOptionTitle } from '../components/viewSettingsLayout/ViewSettingsLayout';

const TableLayoutIcon = (props: IconProps) => {
  return <NewTableIcon {...props} LEGACY_fallbackIcon={OldTableIcon} />;
};

export const getLayoutIcons = (
  type: DatabaseLayoutType,
  iconSize: 'small' | 'xlarge' | 'large' | 'medium' = 'small',
  isDefault = false,
  isPresentationOnly = true,
  isUnsavedView = false
): {
  label: { id: string; defaultMessage: string; description: string };
  icon: ReactElement;
} => {
  if (isUnsavedView) {
    return {
      label: globalMessages['view.unsaved-view'],
      icon: (
        <LayoutIcon
          IconComponent={UnlinkIcon}
          iconSize={iconSize}
          layoutType={globalMessages['view.unsaved-view']}
          isDefault={isDefault}
          isPresentationOnly={true}
        />
      ),
    };
  }

  const layoutOptionTitle = getLayoutOptionTitle(type);

  switch (type) {
    case DatabaseLayoutType.TABLE:
      return {
        label: layoutOptionTitle,
        icon: (
          <LayoutIcon
            IconComponent={TableLayoutIcon}
            iconSize={iconSize}
            layoutType={layoutOptionTitle}
            isDefault={isDefault}
            isPresentationOnly={isPresentationOnly}
          />
        ),
      };
    case DatabaseLayoutType.CARD:
      return {
        label: layoutOptionTitle,
        icon: (
          <LayoutIcon
            IconComponent={MediaServicesGridIcon}
            iconSize={iconSize}
            layoutType={layoutOptionTitle}
            isDefault={isDefault}
            isPresentationOnly={isPresentationOnly}
          />
        ),
      };
    case DatabaseLayoutType.BOARD:
      return {
        label: layoutOptionTitle,
        icon: (
          <LayoutIcon
            IconComponent={BoardIcon}
            iconSize={iconSize}
            layoutType={layoutOptionTitle}
            isDefault={isDefault}
            isPresentationOnly={isPresentationOnly}
          />
        ),
      };
    case DatabaseLayoutType.CALENDAR:
      return {
        label: layoutOptionTitle,
        icon: (
          <LayoutIcon
            IconComponent={CalendarIcon}
            iconSize={iconSize}
            layoutType={layoutOptionTitle}
            isDefault={isDefault}
            isPresentationOnly={isPresentationOnly}
          />
        ),
      };
  }
};

export const getSortIcon = (ascending: boolean) => {
  return ascending ? (
    <ArrowUpIcon LEGACY_size="small" label="" testId="sort-icon-ascending" />
  ) : (
    <ArrowDownIcon LEGACY_size="small" label="" testId="sort-icon-descending" />
  );
};

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;
export const handleSaveView =
  ({
    contentId,
    view,
    viewChangeList,
    unsavedView,
  }: {
    view: DatabaseView;
    contentId: string;
    viewChangeList: ViewChangeType[];
    unsavedView?: DatabaseView;
  }): Dispatcher<void> =>
  (dispatch) => {
    if (unsavedView && isStandalone()) {
      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.SET_SAVED_VIEW,
          view: unsavedView,
        })
      );
    } else {
      const finalViewChangeList = viewChangeList
        .map((change) => getViewChange(change, view))
        .filter((change) => change !== undefined) as DatabaseChange[];

      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
          changes: finalViewChangeList,
        })
      );
    }
  };
